import { DeviceHistoryDataItem, DeviceHistoryDataResponse, getDeviceHistoryData } from '@/api/data/history';
import { ReloadOutlined } from '@ant-design/icons';
import { useRequest } from 'ahooks';
import { Card, Spin, Typography } from 'antd';
import dayjs from 'dayjs';
import { BarChart, BarSeriesOption } from 'echarts/charts';
import {
  GridComponent,
  GridComponentOption,
  LegendComponent,
  LegendComponentOption,
  TooltipComponent,
  TooltipComponentOption,
} from 'echarts/components';
import * as echarts from 'echarts/core';
import { UniversalTransition } from 'echarts/features';
import { CanvasRenderer } from 'echarts/renderers';
import { debounce } from 'lodash';
import React, { useEffect, useRef, useState } from 'react';
import '../style.less';

echarts.use([TooltipComponent, LegendComponent, GridComponent, BarChart, CanvasRenderer, UniversalTransition]);

export type EChartsOption = echarts.ComposeOption<
  TooltipComponentOption | LegendComponentOption | GridComponentOption | BarSeriesOption
>;

export interface DeviceStatisticsTabProps {
  deviceId: string;
}

const DeviceStatisticsTab: React.FC<DeviceStatisticsTabProps> = ({ deviceId }) => {
  const [chartData, setChartData] = useState<DeviceHistoryDataItem[]>([]);
  const chartRef = useRef<HTMLDivElement>(null);
  const echartsRef = useRef<echarts.ECharts | null>(null);

  // 计算最近15天的开始和结束时间
  const endTime = dayjs().format('YYYY-MM-DD');
  const startTime = dayjs().subtract(15, 'day').format('YYYY-MM-DD');

  const { loading, run } = useRequest(
    () => {
      if (!deviceId) {
        return Promise.resolve({
          success: false,
          errorCode: 0,
          errorMsg: '设备ID不存在',
          result: [],
        } as DeviceHistoryDataResponse);
      }
      return getDeviceHistoryData(deviceId, { start_time: startTime, end_time: endTime });
    },
    {
      manual: true,
      onSuccess: (response) => {
        if (response.success && response.result) {
          setChartData(response.result);
        }
      },
    },
  );

  useEffect(() => {
    if (deviceId) {
      run();
    }
  }, [deviceId, run]);

  // 初始化图表
  useEffect(() => {
    if (!chartRef.current) return;

    if (!echartsRef.current) {
      echartsRef.current = echarts.init(chartRef.current, null, {
        renderer: 'canvas',
        devicePixelRatio: 2,
      });
    }

    const resizeFun = () => {
      echartsRef.current?.resize();
    };
    window.addEventListener('resize', debounce(resizeFun, 1000));

    return () => {
      window.removeEventListener('resize', resizeFun);
      if (echartsRef.current) {
        echartsRef.current.dispose();
        echartsRef.current = null;
      }
    };
  }, []);

  // 更新图表数据
  useEffect(() => {
    if (!echartsRef.current) return;
    
    // 如果没有数据，清空图表
    if (!chartData.length) {
      echartsRef.current.clear();
      return;
    }

    // 获取所有唯一的时间点
    const timePoints = Array.from(new Set(chartData.map((item) => item.data_time))).sort();
    
    // 获取所有唯一的类型
    const types = Array.from(new Set(chartData.map((item) => item.type)));

    // 为每个类型创建系列数据
    const series: BarSeriesOption[] = types.map((type) => {
      const data = timePoints.map((time) => {
        const item = chartData.find((d) => d.data_time === time && d.type === type);
        return item ? item.value : 0;
      });

      return {
        name: type,
        type: 'bar' as const,
        data,
        label: {
          align: 'left',
          verticalAlign: 'middle',
          position: 'insideBottom',
          show: true,
          formatter: (params: any) => {
            return params.seriesName;
          },
          rotate: 90,
          fontSize: 12,
        },
      };
    });

    const option: EChartsOption = {
      tooltip: {
        trigger: 'axis',
        axisPointer: {
          type: 'shadow',
        },
      },
      grid: {
        left: '3%',
        right: '4%',
        bottom: '8%',
        top: '5%',
        containLabel: true,
      },
      xAxis: {
        type: 'category',
        data: timePoints,
        show: true,
        axisLine: {
          show: true,
        },
        axisTick: {
          show: true,
        },
        axisLabel: {
          show: true,
          rotate: 45,
          interval: 0,
        },
      },
      yAxis: {
        type: 'value',
        show: true,
        axisLine: {
          show: true,
        },
        axisTick: {
          show: true,
        },
        axisLabel: {
          show: true,
        },
        min: 0,
        splitLine: {
          show: true,
        },
      },
      series,
      barGap: 0,
    };

    echartsRef.current.setOption(option, true);
  }, [chartData]);

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '8px' }}>
        <div
          onClick={() => run()}
          title="刷新"
          style={{
            cursor: 'pointer',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '20px',
            height: '20px',
            border: 'none',
            background: 'transparent',
          }}
        >
          {loading ? (
            <Spin size="small" />
          ) : (
            <ReloadOutlined style={{ fontSize: '16px', color: '#000' }} />
          )}
        </div>
      </div>
      <Card style={{ backgroundColor: '#fff' }}>
        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
            <Spin />
          </div>
        ) : (
          <>
            <div ref={chartRef} style={{ width: '100%', height: '400px' }} />
            <div style={{ marginTop: '6px', textAlign: 'center' }}>
              <Typography.Text type="secondary" style={{ fontSize: '12px' }}>
                展示设备近 15 天每日上报消息数量，便于快速了解设备数据活跃度。
              </Typography.Text>
            </div>
          </>
        )}
      </Card>
    </div>
  );
};

export default DeviceStatisticsTab;

